import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  Box, Container, Typography, Card, Button, Chip, CircularProgress,
  Table, TableBody, TableCell, TableHead, TableRow, Select, MenuItem,
  FormControl, InputLabel, Grid, IconButton, Tooltip, TableSortLabel,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert, LinearProgress, Divider,
  InputAdornment, Menu, ListItemIcon, ListItemText, RadioGroup, FormControlLabel, Radio,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import ReplayIcon from '@mui/icons-material/Replay';
import DownloadIcon from '@mui/icons-material/Download';
import PeopleIcon from '@mui/icons-material/People';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MergeIcon from '@mui/icons-material/MergeType';
import EmailIcon from '@mui/icons-material/Email';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { authedFetch } from '../utils/authedFetch';
import SmsIcon from '@mui/icons-material/Sms';
import EventSmsDialog from '../components/EventSmsDialog';
import { googleContactsCsv } from '../utils/googleContactsCsv';

const FUNCTIONS_BASE = 'https://us-central1-torch-beach-app.cloudfunctions.net';

function fmtDate(iso) {
  if (!iso) return '–';
  const [y,m,d] = iso.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[+m-1]} ${+d}, ${y}`;
}

export default function TournamentAdminRegistrations() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedTid = searchParams.get('tid') || '';
  const [smsOpen, setSmsOpen] = useState(false);
  const [tournaments, setTournaments] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuReg, setMenuReg] = useState(null);
  const [actionDialog, setActionDialog] = useState(null); // { type:'credit'|'refund', reg }
  const [actionBusy, setActionBusy] = useState(false);
  const [actionMsg, setActionMsg] = useState('');
  const [creditMode, setCreditMode] = useState('generic'); // 'generic' | 'apply'
  const [clinics, setClinics] = useState([]);
  const [clinicId, setClinicId] = useState('');
  const [clinicDiv, setClinicDiv] = useState('');
  const [divisionData, setDivisionData] = useState({});
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('asc'); // 'asc' | 'desc'
  const [searchQuery, setSearchQuery] = useState('');

  // Bulk email dialog state
  const [emailDialog, setEmailDialog] = useState(false);
  const [emailFilter, setEmailFilter] = useState('all');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailProgress, setEmailProgress] = useState({ total: 0, sent: 0, failed: 0, current: '' });
  const [emailResult, setEmailResult] = useState(null); // { sent, failed, errors }
  const [emailError, setEmailError] = useState('');

  const handleSort = (col) => {
    if (sortBy === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col);
      setSortDir('asc');
    }
  };

  // Returns a value to sort by for a given registration and column key
  const sortValue = (r, col) => {
    switch (col) {
      case 'tournament': return (r.tournament_name || '').toLowerCase();
      case 'division': return (r.division || '').toLowerCase();
      case 'player1': return `${(r.last_name || '').toLowerCase()} ${(r.first_name || '').toLowerCase()}`;
      case 'partner': {
        const pLast = (r.partner_last_name || '').toLowerCase();
        const pFirst = (r.partner_first_name || '').toLowerCase();
        if (pLast || pFirst) return `${pLast} ${pFirst}`;
        return (r.partner_name || '').toLowerCase();
      }
      case 'email': return (r.email || '').toLowerCase();
      case 'amount': return r.amount_paid || 0;
      case 'status': return (r.status === 'cancelled' ? 'zz_cancelled' : (r.payment_status || ''));
      case 'created_at': {
        const t = r.created_at?.toDate ? r.created_at.toDate() : (r.created_at ? new Date(r.created_at) : new Date(0));
        return t.getTime();
      }
      default: return '';
    }
  };

  const filteredRegistrations = (() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return registrations;
    return registrations.filter(r => {
      const fields = [
        r.first_name, r.last_name, r.email,
        r.partner_first_name, r.partner_last_name, r.partner_email, r.partner_name,
      ];
      return fields.some(v => v && String(v).toLowerCase().includes(q));
    });
  })();

  const sortedRegistrations = [...filteredRegistrations].sort((a, b) => {
    const av = sortValue(a, sortBy);
    const bv = sortValue(b, sortBy);
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  useEffect(() => { loadTournaments(); }, []);
  useEffect(() => { loadRegistrations(); }, [selectedTid]);

  async function loadTournaments() {
    // Newest first, like the admin dashboard: the tournament being picked is
    // nearly always the one just run or about to be, not the oldest on file.
    const snap = await getDocs(query(collection(db, 'tournaments'), orderBy('date', 'desc')));
    setTournaments(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    setLoading(false);
  }

  async function loadRegistrations() {
    setLoading(true);
    try {
      let q = selectedTid
        ? query(collection(db, 'registrations'), where('tournament_id', '==', selectedTid), orderBy('created_at', 'asc'))
        : query(collection(db, 'registrations'), orderBy('created_at', 'asc'));
      const snap = await getDocs(q);
      const regs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setRegistrations(regs);
      if (selectedTid) {
        const tournament = tournaments.find(t => t.id === selectedTid);
        if (tournament) {
          const divs = Array.isArray(tournament.divisions) ? tournament.divisions : JSON.parse(tournament.divisions || '[]');
          const dd = {};
          divs.forEach(div => {
            const divRegs = regs.filter(r => r.division === div && r.status !== 'cancelled');
            dd[div] = {
              count: divRegs.length,
              registrants: divRegs.map(r => ({
                name: r.partner_name ? `${r.first_name} ${r.last_name} & ${r.partner_name}` : `${r.first_name} ${r.last_name}`,
                status: r.payment_status,
              }))
            };
          });
          setDivisionData(dd);
        }
      } else { setDivisionData({}); }
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }

  const openMenu = (e, r) => { setMenuAnchor(e.currentTarget); setMenuReg(r); };
  const closeMenu = () => { setMenuAnchor(null); setMenuReg(null); };

  const openCredit = async (r) => {
    setActionMsg(''); setCreditMode('generic'); setClinicId(''); setClinicDiv('');
    setActionDialog({ type: 'credit', reg: r });
    try {
      const fn = httpsCallable(getFunctions(), 'listFutureClinics');
      const res = await fn({});
      setClinics(res.data?.clinics || []);
    } catch (e) { console.warn('clinic list failed:', e); setClinics([]); }
  };

  const runAction = async () => {
    if (!actionDialog) return;
    setActionBusy(true); setActionMsg('');
    try {
      const fns = getFunctions();
      if (actionDialog.type === 'credit') {
        const fn = httpsCallable(fns, 'creditClinic');
        const payload = { registrationId: actionDialog.reg.id, mode: creditMode };
        if (creditMode === 'apply') { payload.clinicId = clinicId; payload.division = clinicDiv || undefined; }
        const res = await fn(payload);
        setActionMsg(res.data?.msg || 'Done.');
      } else {
        const fn = httpsCallable(fns, 'refundRegistration');
        const res = await fn({ registrationId: actionDialog.reg.id });
        setActionMsg(res.data?.msg || 'Done.');
      }
      await loadRegistrations();
    } catch (e) {
      console.error('action failed:', e);
      setActionMsg('Error: ' + (e.message || e));
    }
    setActionBusy(false);
  };

  const selectedClinic = clinics.find(c => c.id === clinicId);
  const applyReady = creditMode === 'generic' || (creditMode === 'apply' && clinicId);

  // Google Contacts, to get a tournament's entrants into WhatsApp: import into
  // Google Contacts and WhatsApp picks them up from the address book.
  //
  // Tournaments have no teams, so Organization carries the DIVISION — the thing
  // an entrant is grouped by here — and Notes the tournament name. Partners are
  // exported as their own contact when their entry gave a way to reach them;
  // half of them have no number, and a card with neither an email nor a phone
  // imports as an empty contact, so those are dropped.
  function exportGoogleContacts() {
    const t = tournaments.find(x => x.id === selectedTid);
    const notes = t?.name || 'Tournament';
    const rows = [];
    registrations.filter(r => r.status !== 'cancelled').forEach(r => {
      rows.push({
        firstName: r.first_name, lastName: r.last_name,
        email: r.email, phone: r.phone,
        notes, organization: r.division || '',
      });
      if (r.partner_phone || r.partner_email) {
        rows.push({
          firstName: r.partner_first_name || (r.partner_name || '').split(' ')[0] || '',
          lastName: r.partner_last_name || (r.partner_name || '').split(' ').slice(1).join(' '),
          email: r.partner_email, phone: r.partner_phone,
          notes, organization: r.division || '',
        });
      }
    });
    const blob = new Blob(['\ufeff' + googleContactsCsv(rows)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${String(notes).replace(/\s+/g, '_')}_contacts.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function exportCSV(div) {
    const regs = registrations.filter(r => r.division === div && r.status !== 'cancelled');
    const lines = ['Names,Points'];
    regs.forEach(r => {
      const p1 = r.first_name?.trim() || '';
      const p2First = r.partner_name?.split(' ')[0] || '';
      lines.push(`"${p1}${p2First ? ' / '+p2First : ''}",""`);
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const tName = (tournaments.find(t => t.id === selectedTid)?.name || 'tournament').replace(/\s+/g,'_');
    a.href = url; a.download = `${tName}_${div.replace(/\s+/g,'_')}.csv`; a.click();
    URL.revokeObjectURL(url);
  }

  // Compute recipient count for current filter (for the dialog UI)
  function recipientCount(filter) {
    const active = registrations.filter(r => r.status !== 'cancelled' && r.email);
    switch (filter) {
      case 'all':       return active.length;
      case 'paid':      return active.filter(r => r.payment_status === 'paid').length;
      case 'pending':   return active.filter(r => r.payment_status === 'pending' || !r.payment_status).length;
      case 'half_paid': return active.filter(r => r.payment_status === 'half_paid').length;
      case 'waitlist':  return active.filter(r => (r.waitlist_position || 0) > 0).length;
      default:          return active.filter(r => r.division === filter).length;
    }
  }

  function openEmailDialog() {
    const t = tournaments.find(t => t.id === selectedTid);
    setEmailSubject(t?.name ? `${t.name} — Update` : '');
    setEmailBody('Hi {first_name},\n\n\n\nThanks,\nTorch Beach Volleyball');
    setEmailFilter('all');
    setEmailResult(null);
    setEmailError('');
    setEmailProgress({ total: 0, sent: 0, failed: 0, current: '' });
    setEmailDialog(true);
  }

  // Send via Server-Sent Events to get live progress
  async function sendBulkEmail({ testMode = false } = {}) {
    if (!emailSubject.trim() || !emailBody.trim()) {
      setEmailError('Please fill in both subject and body.');
      return;
    }
    if (testMode) {
      // For test mode, no need to stream — just one email
    }
    setEmailSending(true);
    setEmailError('');
    setEmailResult(null);
    setEmailProgress({ total: 0, sent: 0, failed: 0, current: '' });
    try {
      const tournament = tournaments.find(t => t.id === selectedTid);
      const replyTo = tournament?.director_email || tournament?.directorEmail || undefined;
      const payload = {
        tournament_id: selectedTid,
        filter: emailFilter,
        subject: emailSubject,
        body: emailBody,
        reply_to: replyTo,
      };
      if (testMode) payload.test_to = 'stevenklong@gmail.com';

      const res = await authedFetch(`${FUNCTIONS_BASE}/bulkEmailRegistrants`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'text/event-stream' },
        body: JSON.stringify(payload),
      });
      if (!res.ok || !res.body) {
        const text = await res.text();
        setEmailError(`Send failed: ${text || res.statusText}`);
        setEmailSending(false);
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        // Parse SSE events: "event: name\ndata: json\n\n"
        const events = buffer.split('\n\n');
        buffer = events.pop(); // keep last partial chunk
        for (const ev of events) {
          const lines = ev.split('\n');
          let evName = '';
          let dataStr = '';
          for (const ln of lines) {
            if (ln.startsWith('event: ')) evName = ln.slice(7).trim();
            else if (ln.startsWith('data: ')) dataStr += ln.slice(6);
          }
          if (!evName) continue;
          let data; try { data = JSON.parse(dataStr); } catch { data = {}; }
          if (evName === 'start') {
            setEmailProgress(p => ({ ...p, total: data.total }));
          } else if (evName === 'progress') {
            setEmailProgress(p => ({ ...p, sent: data.sent, failed: data.failed, current: data.current, total: data.of || p.total }));
          } else if (evName === 'done') {
            setEmailResult(data);
          } else if (evName === 'error') {
            setEmailError(data.message || 'Send failed.');
          }
        }
      }
    } catch (e) {
      setEmailError('Send failed: ' + (e.message || 'Unknown error'));
    } finally {
      setEmailSending(false);
    }
  }

  const paymentChip = {
    paid:      <Chip label="Paid" color="success" size="small" />,
    half_paid: <Chip label="Half Paid" color="warning" size="small" />,
    pending:   <Chip label="Pay Later" size="small" />,
  };

  return (
    <Box>
      <Box sx={{ bgcolor: '#1a2940', color: '#fff', py: 4, mb: 4 }}>
        <Container sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4" fontWeight="bold">👥 Registrations</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {selectedTid && (
              <Button variant="outlined" color="inherit" startIcon={<DownloadIcon />}
                      onClick={exportGoogleContacts}
                      sx={{ borderColor: 'rgba(255,255,255,0.4)', '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.08)' } }}>
                Contacts CSV
              </Button>
            )}
            {selectedTid && (
              <Button variant="outlined" color="inherit" startIcon={<SmsIcon />}
                      onClick={() => setSmsOpen(true)}
                      sx={{ borderColor: 'rgba(255,255,255,0.4)', '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.08)' } }}>
                Text Registrants
              </Button>
            )}
            {selectedTid && (
              <Button variant="outlined" color="inherit" startIcon={<EmailIcon />}
                      onClick={openEmailDialog}
                      sx={{ borderColor: 'rgba(255,255,255,0.4)', '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.08)' } }}>
                Email Registrants
              </Button>
            )}
            <Button variant="outlined" color="warning" startIcon={<MergeIcon />}
                    onClick={() => navigate('/tournament-admin/merge-divisions')}>Merge Divisions</Button>
          </Box>
        </Container>
      </Box>
      <EventSmsDialog open={smsOpen} onClose={() => setSmsOpen(false)}
        kind="tournament" eventId={selectedTid}
        eventName={tournaments.find(t => t.id === selectedTid)?.name || 'Tournament'} />

      <Container sx={{ pb: 6 }}>

        {/* Tournament selector */}
        <Card elevation={2} sx={{ borderRadius: 3, mb: 4, p: 3 }}>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Select Tournament</InputLabel>
                <Select value={selectedTid} label="Select Tournament"
                        onChange={e => setSearchParams(e.target.value ? { tid: e.target.value } : {})}>
                  <MenuItem value="">— All Tournaments —</MenuItem>
                  {tournaments.map(t => <MenuItem key={t.id} value={t.id}>{t.name} ({fmtDate(t.date)})</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: selectedTid ? 4 : 6 }}>
              <TextField fullWidth size="small" placeholder="Search players (name or email)..."
                         value={searchQuery}
                         onChange={e => setSearchQuery(e.target.value)}
                         InputProps={{
                           startAdornment: (
                             <InputAdornment position="start">
                               <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                             </InputAdornment>
                           ),
                           endAdornment: searchQuery ? (
                             <InputAdornment position="end">
                               <IconButton size="small" onClick={() => setSearchQuery('')}>
                                 <ClearIcon fontSize="small" />
                               </IconButton>
                             </InputAdornment>
                           ) : null,
                         }} />
            </Grid>
            {selectedTid && (
              <Grid size={{ xs: 12, sm: 2 }}>
                <Button variant="outlined" fullWidth onClick={() => setSearchParams({})}>Clear</Button>
              </Grid>
            )}
          </Grid>
        </Card>

        {/* Table */}
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 5 }}><CircularProgress /></Box>
        ) : registrations.length > 0 ? (
          <Card elevation={2} sx={{ borderRadius: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#1a2940' }}>
                  {[
                    { label: 'Tournament', key: 'tournament' },
                    { label: 'Division', key: 'division' },
                    { label: 'Player 1', key: 'player1' },
                    { label: 'Partner', key: 'partner' },
                    { label: 'Email', key: 'email' },
                    { label: 'Amount', key: 'amount' },
                    { label: 'Status', key: 'status' },
                    { label: 'Date', key: 'created_at' },
                    { label: '', key: null },
                  ].map(h => (
                    <TableCell key={h.label + h.key} sx={{ color: '#fff', fontWeight: 'bold' }}>
                      {h.key ? (
                        <TableSortLabel
                          active={sortBy === h.key}
                          direction={sortBy === h.key ? sortDir : 'asc'}
                          onClick={() => handleSort(h.key)}
                          sx={{
                            color: '#fff !important',
                            '& .MuiTableSortLabel-icon': { color: '#fff !important' },
                            '&:hover': { color: '#C9A84C' },
                            '&.Mui-active': { color: '#C9A84C' },
                          }}
                        >
                          {h.label}
                        </TableSortLabel>
                      ) : h.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedRegistrations.map(r => (
                  <TableRow key={r.id} sx={{ opacity: r.status === 'cancelled' ? 0.5 : 1, textDecoration: r.status === 'cancelled' ? 'line-through' : 'none' }}>
                    <TableCell><Typography variant="caption" fontWeight="bold">{r.tournament_name}</Typography></TableCell>
                    <TableCell><Chip label={r.division} size="small" /></TableCell>
                    <TableCell><Typography variant="caption">{r.first_name} {r.last_name}</Typography></TableCell>
                    <TableCell><Typography variant="caption">{r.partner_name || '–'}</Typography></TableCell>
                    <TableCell><Typography variant="caption">{r.email}</Typography></TableCell>
                    <TableCell><Typography variant="caption">${r.amount_paid ? (r.amount_paid/100).toFixed(2) : '0.00'}</Typography></TableCell>
                    <TableCell>
                      {r.status === 'cancelled' ? <Chip label="Cancelled" color="error" size="small" /> : (paymentChip[r.payment_status] || null)}
                      {r.waitlist_position > 0 && <Chip label={`WL#${r.waitlist_position}`} color="warning" size="small" sx={{ ml: 0.5 }} />}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {r.created_at?.toDate ? r.created_at.toDate().toLocaleDateString() : r.created_at?.slice(0,10) || '–'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={(e) => openMenu(e, r)}>
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        ) : (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography color="text.secondary">No registrations found.</Typography>
          </Box>
        )}

        <Box sx={{ mt: 3 }}>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/tournament-admin')}>
            Back to Dashboard
          </Button>
        </Box>
      </Container>

      {/* Row action kebab */}
      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={closeMenu}>
        <MenuItem onClick={() => { const r = menuReg; closeMenu(); navigate(`/tournament-admin/registration/${r.id}`); }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem disabled={menuReg?.status === 'cancelled'}
          onClick={() => { const r = menuReg; closeMenu(); openCredit(r); }}>
          <ListItemIcon><CardGiftcardIcon fontSize="small" color="success" /></ListItemIcon>
          <ListItemText>Clinic Credit</ListItemText>
        </MenuItem>
        <MenuItem disabled={menuReg?.status === 'cancelled'}
          onClick={() => { const r = menuReg; closeMenu(); setActionMsg(''); setActionDialog({ type: 'refund', reg: r }); }}>
          <ListItemIcon><ReplayIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Refund</ListItemText>
        </MenuItem>
      </Menu>

      {/* Credit / Refund dialog */}
      <Dialog open={!!actionDialog} onClose={() => !actionBusy && setActionDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ bgcolor: '#1a2940', color: '#fff' }}>
          {actionDialog?.type === 'credit' ? 'Clinic Credit' : 'Refund Registration'}
        </DialogTitle>
        <DialogContent sx={{ pt: '20px !important' }}>
          {actionMsg ? (
            <Alert severity={actionMsg.startsWith('Error') ? 'error' : 'success'}>{actionMsg}</Alert>
          ) : actionDialog?.type === 'credit' ? (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>{actionDialog?.reg?.first_name} {actionDialog?.reg?.last_name}</strong> ({actionDialog?.reg?.email})
              </Typography>
              <RadioGroup value={creditMode} onChange={e => setCreditMode(e.target.value)}>
                <FormControlLabel value="generic" control={<Radio size="small" />}
                  label="Grant 1 clinic credit (redeem later)" />
                <FormControlLabel value="apply" control={<Radio size="small" />}
                  label="Apply to a specific clinic now (comped)" />
              </RadioGroup>
              {creditMode === 'apply' && (
                <Box sx={{ mt: 1 }}>
                  <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                    <InputLabel>Future Clinic</InputLabel>
                    <Select value={clinicId} label="Future Clinic"
                      onChange={e => { setClinicId(e.target.value); setClinicDiv(''); }}>
                      {clinics.length === 0 && <MenuItem value="" disabled>No upcoming clinics</MenuItem>}
                      {clinics.map(c => <MenuItem key={c.id} value={c.id}>{c.name} ({c.date})</MenuItem>)}
                    </Select>
                  </FormControl>
                  {selectedClinic && selectedClinic.divisions.length > 1 && (
                    <FormControl fullWidth size="small">
                      <InputLabel>Division</InputLabel>
                      <Select value={clinicDiv} label="Division" onChange={e => setClinicDiv(e.target.value)}>
                        {selectedClinic.divisions.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                      </Select>
                    </FormControl>
                  )}
                </Box>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                This cancels their tournament entry and marks it credited.
              </Typography>
            </>
          ) : (
            <Typography variant="body2">
              Refund <strong>${actionDialog ? ((actionDialog.reg.amount_paid || 0) / 100).toFixed(2) : ''}</strong> to <strong>{actionDialog?.reg?.first_name} {actionDialog?.reg?.last_name}</strong> via Stripe and cancel their registration? This moves money and can't be undone here.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          {actionMsg ? (
            <Button onClick={() => { setActionDialog(null); setActionMsg(''); }}>Close</Button>
          ) : (
            <>
              <Button disabled={actionBusy} onClick={() => setActionDialog(null)}>Cancel</Button>
              <Button variant="contained" disabled={actionBusy || (actionDialog?.type === 'credit' && !applyReady)}
                color={actionDialog?.type === 'refund' ? 'error' : 'success'}
                onClick={runAction}>
                {actionBusy ? <CircularProgress size={20} color="inherit" />
                  : actionDialog?.type === 'refund' ? 'Issue Refund'
                  : creditMode === 'apply' ? 'Register (Comped)' : 'Grant Credit'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Bulk Email Dialog */}
      <Dialog open={emailDialog} onClose={() => !emailSending && setEmailDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: '#1a2940', color: '#fff' }}>
          📧 Email Registrants — {tournaments.find(t => t.id === selectedTid)?.name || ''}
        </DialogTitle>
        <DialogContent sx={{ pt: '20px !important' }}>
          {emailError && <Alert severity="error" sx={{ mb: 2 }}>{emailError}</Alert>}
          {emailResult && (
            <Alert severity={emailResult.failed > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
              Sent {emailResult.sent} email{emailResult.sent !== 1 ? 's' : ''}
              {emailResult.failed > 0 ? `, ${emailResult.failed} failed.` : '.'}
              {emailResult.errors?.length > 0 && (
                <Box sx={{ mt: 1, fontSize: '0.8rem' }}>
                  {emailResult.errors.slice(0, 5).map((err, i) => (
                    <div key={i}>• {err.email}: {err.message}</div>
                  ))}
                  {emailResult.errors.length > 5 && <div>...and {emailResult.errors.length - 5} more</div>}
                </Box>
              )}
            </Alert>
          )}

          {/* Filter */}
          <FormControl fullWidth size="small" sx={{ mb: 2 }} disabled={emailSending}>
            <InputLabel>Recipients</InputLabel>
            <Select label="Recipients" value={emailFilter} onChange={e => setEmailFilter(e.target.value)}>
              <MenuItem value="all">All registrants ({recipientCount('all')})</MenuItem>
              <MenuItem value="paid">Paid only ({recipientCount('paid')})</MenuItem>
              <MenuItem value="pending">Pending payment ({recipientCount('pending')})</MenuItem>
              <MenuItem value="half_paid">Half paid ({recipientCount('half_paid')})</MenuItem>
              <MenuItem value="waitlist">Waitlist only ({recipientCount('waitlist')})</MenuItem>
              <Divider />
              {(() => {
                const t = tournaments.find(t => t.id === selectedTid);
                if (!t) return null;
                const divs = Array.isArray(t.divisions) ? t.divisions : JSON.parse(t.divisions || '[]');
                return divs.map(div => (
                  <MenuItem key={div} value={div}>{div} ({recipientCount(div)})</MenuItem>
                ));
              })()}
            </Select>
          </FormControl>

          {/* Subject */}
          <TextField fullWidth size="small" label="Subject" value={emailSubject}
                     onChange={e => setEmailSubject(e.target.value)} sx={{ mb: 2 }} disabled={emailSending} />

          {/* Body */}
          <TextField fullWidth multiline minRows={8} maxRows={20} label="Message (Markdown supported)" value={emailBody}
                     onChange={e => setEmailBody(e.target.value)} disabled={emailSending}
                     helperText="Markdown: **bold**, *italic*, [link text](https://...), # heading, - bullets, 1. numbered. Variables: {first_name}, {last_name}, {partner_name}, {division}, {tournament_name}, {payment_status}, {amount_due}, {cancel_link}, {pay_link}. Pay Now button auto-included for unpaid registrations." />

          {/* Progress */}
          {emailSending && emailProgress.total > 0 && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress variant="determinate"
                value={emailProgress.total ? ((emailProgress.sent + emailProgress.failed) / emailProgress.total) * 100 : 0} />
              <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: 'text.secondary' }}>
                Sending {emailProgress.sent + emailProgress.failed} of {emailProgress.total}
                {emailProgress.failed > 0 ? ` (${emailProgress.failed} failed)` : ''}
                {emailProgress.current ? ` · ${emailProgress.current}` : ''}
              </Typography>
            </Box>
          )}
          {emailSending && emailProgress.total === 0 && (
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">Preparing send...</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDialog(false)} disabled={emailSending}>Close</Button>
          <Button onClick={() => sendBulkEmail({ testMode: true })} disabled={emailSending} variant="outlined">
            Send Test to stevenklong@gmail.com
          </Button>
          <Button onClick={() => sendBulkEmail()} disabled={emailSending || recipientCount(emailFilter) === 0}
                  variant="contained" color="primary">
            {emailSending ? 'Sending...' : `Send to ${recipientCount(emailFilter)}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
